const loadMissDirectoryMap = () => {
  if (window.L) return Promise.resolve(window.L);
  if (window.missDirectoryLeafletPromise) return window.missDirectoryLeafletPromise;

  window.missDirectoryLeafletPromise = new Promise((resolve, reject) => {
    if (!document.querySelector('link[data-miss-directory-leaflet]')) {
      const stylesheet = document.createElement('link');
      stylesheet.rel = 'stylesheet';
      stylesheet.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      stylesheet.dataset.missDirectoryLeaflet = 'true';
      document.head.append(stylesheet);
    }

    const existingScript = document.querySelector('script[data-miss-directory-leaflet]');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.L), { once: true });
      existingScript.addEventListener('error', reject, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.dataset.missDirectoryLeaflet = 'true';
    script.addEventListener('load', () => resolve(window.L), { once: true });
    script.addEventListener('error', reject, { once: true });
    document.head.append(script);
  });

  return window.missDirectoryLeafletPromise;
};

class MissCollaboratorDirectory {
  constructor(root) {
    this.root = root;
    this.cards = Array.from(root.querySelectorAll('[data-directory-card]'));
    this.province = root.querySelector('[data-directory-province]');
    this.city = root.querySelector('[data-directory-city]');
    this.clear = root.querySelector('[data-directory-clear]');
    this.results = root.querySelector('[data-directory-results]');
    this.empty = root.querySelector('[data-directory-empty]');
    this.mapToggle = root.querySelector('[data-directory-map-toggle]');
    this.mapToggleLabel = root.querySelector('[data-directory-map-toggle-label]');
    this.mapPanel = root.querySelector('[data-directory-map-panel]');
    this.mapElement = root.querySelector('[data-directory-map]');
    this.map = null;
    this.mapMarkers = [];

    this.mapToggle?.addEventListener('click', () => this.toggleMap());

    if (!this.province || !this.city) return;

    this.mobileMedia = window.matchMedia('(max-width: 749px)');
    this.handleResponsiveLabels = () => this.updateDefaultLabels();
    this.updateDefaultLabels();
    this.populateSelect(this.province, this.uniqueValues('province'));
    this.populateCities();
    this.province.addEventListener('change', () => {
      this.city.value = '';
      this.populateCities();
      this.filter();
    });
    this.city.addEventListener('change', () => this.filter());
    this.clear?.addEventListener('click', () => this.reset());
    if (this.mobileMedia.addEventListener) {
      this.mobileMedia.addEventListener('change', this.handleResponsiveLabels);
    } else {
      this.mobileMedia.addListener(this.handleResponsiveLabels);
    }
    this.filter();
  }

  updateDefaultLabels() {
    const labelKey = this.mobileMedia.matches ? 'labelMobile' : 'labelWide';
    const provinceLabel = this.province.dataset[labelKey] || this.province.options[0]?.textContent || '';
    const cityLabel = this.city.dataset[labelKey] || this.city.options[0]?.textContent || '';

    if (this.province.options[0]) this.province.options[0].textContent = provinceLabel;
    if (this.city.options[0]) this.city.options[0].textContent = cityLabel;
    this.allCitiesLabel = cityLabel;
  }

  escapeHtml(value) {
    const element = document.createElement('span');
    element.textContent = value || '';
    return element.innerHTML;
  }

  getMapPoints() {
    return this.cards.flatMap((card) =>
      Array.from(card.querySelectorAll('[data-directory-map-point]'))
        .map((point) => ({
          card,
          latitude: Number.parseFloat(point.dataset.latitude),
          longitude: Number.parseFloat(point.dataset.longitude),
          label: point.dataset.label || '',
          name: point.dataset.name || '',
          role: point.dataset.role || '',
          googleUrl: point.dataset.googleUrl || '',
        }))
        .filter((point) => Number.isFinite(point.latitude) && Number.isFinite(point.longitude))
    );
  }

  async toggleMap() {
    if (!this.mapPanel || !this.mapToggle || !this.mapElement) return;

    const willOpen = this.mapPanel.hidden;
    this.mapPanel.hidden = !willOpen;
    this.mapToggle.setAttribute('aria-expanded', String(willOpen));
    if (this.mapToggleLabel) {
      this.mapToggleLabel.textContent = willOpen ? this.root.dataset.mapHideLabel : this.root.dataset.mapShowLabel;
    }

    if (!willOpen) return;

    try {
      await this.initializeMap();
      window.requestAnimationFrame(() => {
        this.map?.invalidateSize();
        this.updateMapVisibility();
      });
    } catch (error) {
      this.mapElement.textContent = this.root.dataset.mapShowLabel;
    }
  }

  async initializeMap() {
    if (this.map) return;
    const points = this.getMapPoints();
    if (!points.length) return;

    const L = await loadMissDirectoryMap();
    this.map = L.map(this.mapElement, { scrollWheelZoom: false, tap: true });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap',
    }).addTo(this.map);

    this.mapMarkers = points.map((point) => {
      const markerLetter = this.escapeHtml((point.role || point.name).slice(0, 1).toUpperCase());
      const icon = L.divIcon({
        className: '',
        html: `<div class="mec-map-pin"><span>${markerLetter}</span></div>`,
        iconSize: [28, 28],
        iconAnchor: [14, 28],
        popupAnchor: [0, -26],
      });
      const marker = L.marker([point.latitude, point.longitude], { icon });
      const mapLink = point.googleUrl
        ? `<a class="mec-map-popup__link" href="${this.escapeHtml(point.googleUrl)}" target="_blank" rel="noopener">${this.escapeHtml(this.root.dataset.mapGoogleLabel)} ↗</a>`
        : '';
      marker.bindPopup(
        `<span class="mec-map-popup__name">${this.escapeHtml(point.name)}</span>` +
          `<span class="mec-map-popup__role">${this.escapeHtml(point.role)}</span>` +
          `<span class="mec-map-popup__place">${this.escapeHtml(point.label)}</span>` +
          mapLink
      );
      return { marker, card: point.card };
    });
  }

  updateMapVisibility() {
    if (!this.map) return;
    const visibleLatLngs = [];

    this.mapMarkers.forEach(({ marker, card }) => {
      if (card.hidden) {
        if (this.map.hasLayer(marker)) this.map.removeLayer(marker);
        return;
      }
      if (!this.map.hasLayer(marker)) marker.addTo(this.map);
      visibleLatLngs.push(marker.getLatLng());
    });

    if (visibleLatLngs.length === 1) {
      this.map.setView(visibleLatLngs[0], 12);
    } else if (visibleLatLngs.length > 1) {
      this.map.fitBounds(visibleLatLngs, { padding: [34, 34], maxZoom: 12 });
    }
  }

  normalize(value) {
    return (value || '').trim().toLocaleLowerCase('es');
  }

  uniqueValues(key, cards = this.cards) {
    return [...new Set(cards.map((card) => card.dataset[key]).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
  }

  populateSelect(select, values) {
    values.forEach((value) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = value;
      select.append(option);
    });
  }

  populateCities() {
    const selectedProvince = this.normalize(this.province.value);
    const eligibleCards = selectedProvince
      ? this.cards.filter((card) => this.normalize(card.dataset.province) === selectedProvince)
      : this.cards;

    this.city.replaceChildren(new Option(this.allCitiesLabel, ''));
    this.populateSelect(this.city, this.uniqueValues('city', eligibleCards));
  }

  filter() {
    const selectedProvince = this.normalize(this.province.value);
    const selectedCity = this.normalize(this.city.value);
    let visible = 0;

    this.cards.forEach((card) => {
      const provinceMatches = !selectedProvince || this.normalize(card.dataset.province) === selectedProvince;
      const cityMatches = !selectedCity || this.normalize(card.dataset.city) === selectedCity;
      card.hidden = !(provinceMatches && cityMatches);
      if (!card.hidden) visible += 1;
    });

    const filtersActive = Boolean(selectedProvince || selectedCity);
    if (this.clear) this.clear.hidden = !filtersActive;
    if (this.empty) this.empty.hidden = visible !== 0;
    if (this.results) this.results.textContent = this.resultText(visible);
    this.updateMapVisibility();
  }

  resultText(count) {
    if (count === 1) return '1 resultado';
    return `${count} resultados`;
  }

  reset() {
    this.province.value = '';
    this.city.value = '';
    this.populateCities();
    this.filter();
  }
}

const initializeMissDirectories = (scope = document) => {
  scope.querySelectorAll('[data-collaborator-directory]').forEach((root) => {
    if (root.dataset.directoryReady === 'true') return;
    root.dataset.directoryReady = 'true';
    new MissCollaboratorDirectory(root);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeMissDirectories());
} else {
  initializeMissDirectories();
}

document.addEventListener('shopify:section:load', (event) => initializeMissDirectories(event.target));
